
import 'scroll-behavior-polyfill'
import throttle from 'lodash/throttle'
import scssSpacingVariables from '../../styles/variables/exports/spacing.scss?module'
import EoButton from '../EoButton/EoButton.vue'
import EoIcon from '../EoIcon/EoIcon.vue'
import EoSpinner from '../EoSpinner/EoSpinner.vue'

const interactionBreakpoint = 'md'

export default {
  name: 'EoList',
  components: { EoButton, EoIcon, EoSpinner },
  props: {
    /**
     * SOON DEPRECATED: Remove the gaps between items
     */
    gapless: {
      type: Boolean,
      default: false,
    },
    /**
     * Gaps between items (0, 1, 2, 3, 4, 5, 6)
     */
    gap: {
      type: [String, Number, Object],
      default: '3',
    },
    /**
     * Let the list know that (more) items are being loaded
     */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * Changes the list into a horizontal carousel
     */
    carousel: {
      type: Boolean,
      default: false,
    },
    /**
     * Carousel: width of each item: (1-12 OR pixels)
     */
    itemSize: {
      type: [Object, String, Number],
      default: null,
      validator: (value) => {
        if (Number(value)) {
          return 1 <= Number(value) && Number(value) <= 12
        }
        return true
      },
    },
    /**
     * Carousel: overflow horizontally: (fullwidth, 1, 2, 3, 4, 5)
     */
    overflowX: {
      type: [String, Number],
      default: null,
      validator (amount) {
        return amount == 'fullwidth' || (1 <= amount && amount <= 5)
      },
    },
    /**
     * Carousel: overflow vertically: (1, 2, 3, 4, 5)
     */
    overflowY: {
      type: [String, Number],
      default: null,
      validator (amount) {
        return  1 <= amount && amount <= 5
      },
    },
    /**
     * Carousel: size of the scroll buttons: (xs) (sm) (md)
     */
    buttonsSize: {
      type: String,
      validator: prop => Object.values(['xs', 'sm', 'md']).includes(prop),
      default: 'sm',
    },
    /**
     * Carousel: place the scroll buttons inside the list boundaries
     */
    buttonsInline: {
      type: Boolean,
      default: false,
    },
    /**
     * Carousel: color of the scroll buttons
     */
    buttonsColor: {
      type: String,
      default: 'white',
    },
    /**
     * Carousel: icon color of the scroll buttons
     */
    buttonsTextColor: {
      type: String,
      default: null,
    },
  },
  data () {
    return {
      items: [],
      hasHeadingSlot: false,
      carouselItemsVisible: [],
      carouselEdgeLeft: 0,
      carouselEdgeRight: 0,
      carouselScrollMax: 0,
      carouselPreviousLoadMore: 0,
      carouselScrollProgress: 0,
      horizontalOverflow: 0,
      verticalOverflow: 0,
      isBrowserNotChrome: null,
    }
  },
  computed: {
    classes () {
      return {
        'eo-list': true,
        'is-gapless': this.currentGapSize == 0 || this.gapless,
        'is-carousel': this.carousel,
        'is-buttons-inline': this.buttonsInline,
        'has-overflow': this.hasOverflow,
      }
    },
    hasMoreItemsLeft () {
      return this.carousel && this.carouselScrollMax > 0 && this.carouselScrollProgress > 0
    },
    hasMoreItemsRight () {
      return this.carousel && this.carouselScrollMax > 0 && this.carouselScrollProgress < 100
    },
    hasOverflow () {
      return this.hasMoreItemsLeft || this.hasMoreItemsRight
    },
    container () {
      let containerComponent = null
      let parent = this.$parent
      while (parent && !containerComponent) {
        if (parent.$options?.name === 'EoContainer') {
          containerComponent = parent
        }
        parent = parent.$parent
      }
      return containerComponent
    },
    spacingHorizontal () {
      return this.horizontalOverflow
    },
    spacingVertical () {
      return this.verticalOverflow
    },
    style () {
      return {
        // inline styles for .eo-list
        marginLeft: `-${this.spacingHorizontal}px`,
        marginRight: `-${this.spacingHorizontal}px`,
        '--carousel-item-width-xs': this.getCarouselItemWidth('xs'),
        '--carousel-item-width-sm': this.getCarouselItemWidth('sm'),
        '--carousel-item-width-md': this.getCarouselItemWidth('md'),
        '--carousel-item-width-lg': this.getCarouselItemWidth('lg'),
        '--carousel-item-width-xl': this.getCarouselItemWidth('xl'),
        '--gap-xs': this.getGapProperty('xs'),
        '--gap-sm': this.getGapProperty('sm'),
        '--gap-md': this.getGapProperty('md'),
        '--gap-lg': this.getGapProperty('lg'),
        '--gap-xl': this.getGapProperty('xl'),
      }
    },
    styleHeading () {
      return {
        // inline styles for .eo-list__heading
        paddingLeft: `${this.spacingHorizontal}px`,
        paddingRight: `${this.spacingHorizontal}px`,
      }
    },
    styleContent () {
      return {
        // inline styles for .eo-list__content
        marginBottom: this.spacingVertical ? `-${this.spacingVertical}px` : null,
        marginTop: this.spacingVertical ? `-${this.spacingVertical}px` : null,
        paddingLeft: `${this.spacingHorizontal}px`,
        paddingRight: `${this.spacingHorizontal}px`,
        paddingTop: `${this.spacingVertical}px`,
        paddingBottom: `${this.spacingVertical}px`,
      }
    },
    styleCarousel () {
      return {
        // inline styles for .eo-list__carousel
        marginBottom: `-${this.spacingVertical}px`,
        marginTop: `-${this.spacingVertical}px`,
        paddingLeft: `${this.spacingHorizontal}px`,
        paddingRight: `${this.spacingHorizontal}px`,
        scrollPaddingLeft: `${this.spacingHorizontal}px`,
        paddingTop: `${this.spacingVertical}px`,
        paddingBottom: `${this.spacingVertical}px`,
      }
    },
    currentGapSize () {
      if (this.gapless) return '0'
      if (this.gap == null || this.gap == undefined) return '3'
      if (typeof this.gap === 'object') return this.$viewport.switch(this.gap)
      return this.gap
    },
  },
  watch: {
    /**
     * Watch changes in width of EoList parent container
     */
    'container.width': {
      handler: function () {
        this.calculateOverflow()
        this.trackCarouselState()
      },
      immediate: true,
    },
  },
  mounted () {
    // still needed for browsers not adding padding to scroll container
    this.isBrowserNotChrome = navigator.appVersion.indexOf("Chrome/") === -1

    this.checkHeadingSlot()
    this.initiateCarouselItems()
    this.calculateOverflow()
    this.trackCarouselState()
    this.checkCarouselLoadMore()
  },
  updated () {
    this.checkHeadingSlot()
    this.initiateCarouselItems()
    this.trackCarouselState()
    this.checkCarouselLoadMore()
  },
  methods: {
    getGapProperty (breakpoint) {
      let gapSize = ''
      if (typeof this.gap === 'object' && breakpoint) {
        gapSize = this.$viewport.switch(this.gap, breakpoint)
      } else {
        if (this.gapless || this.gap == 0) {
          gapSize = 0
        } else if (!this.gap) {
          gapSize = 3
        } else {
          gapSize = this.currentGapSize
        }
      }
      return `var(--spacing-${gapSize})`
    },
    getCarouselItemWidth (breakpoint) {
      if (!this.itemSize) return null
      let itemWidth = this.itemSize
      if (typeof this.itemSize === 'object' && breakpoint) {
        itemWidth = this.$viewport.switch(this.itemSize, breakpoint)
      }

      const amountItems = 12 / Number(itemWidth)
      const amountGaps = amountItems - 1

      // Convert to css property
      if (this.currentGapSize == 0) {
        return `calc(${100 / amountItems}%)`
      } else if (Number(itemWidth)) {
        return `calc(${100 / amountItems}% - (${this.getGapProperty()} * ${amountGaps}) / ${amountItems})`
      } else {
        return itemWidth
      }
    },
    checkHeadingSlot () {
      this.hasHeadingSlot = !!this.$slots.heading
    },
    async calculateOverflow () {
      /**
       * Calculate the amount of whitespace that needs to be overflown
       */
      if (!this.carousel) return
      if (this.container === null) return
      if (this.container.width == 0) return
      if (this.$el === undefined) return
      if (!this.$el.offsetWidth) return

      if (this.overflowX) {
        if (this.overflowX == 'fullwidth') {
          // Overflow an area as wide as the EoContainer's parent
          const initialWidth = this.$el.offsetWidth - (2 * this.spacingHorizontal)
          this.horizontalOverflow = Number((this.container.width - initialWidth) * 0.5)
        } else {
          // Overflow an area by fixed amount
          const spacing = scssSpacingVariables[`spacing-${this.overflowX}`]
          this.horizontalOverflow = Number(spacing ? spacing.replace('px', '') : 0)
        }
      }

      if (this.overflowY) {
        // Overflow an area by fixed amount
        const spacing = scssSpacingVariables[`spacing-${this.overflowY}`]
        this.verticalOverflow = Number(spacing ? spacing.replace('px', '') : 0)
      }
    },
    async initiateCarouselItems () {
      /**
       * Make sure the items are presented nicely
       */
      if (!this.carousel) return
      if (!this.$slots.default || !this.$slots.default.length) return
      let items = []
      this.$slots.default.forEach(child => {
        if (child.text !== undefined || child.isComment) return
        if (child?.data?.attrs?.['eo-list-exclude'] === '') return
        if (child?.data?.attrs?.['eo-list-exclude'] === true) return
        const item = child.componentInstance !== undefined ? child.componentInstance : child
        items.push(item)
      })
      this.items = items
    },
    trackCarouselState () {
      /** 
       * Calculate state of the carousel
       */
      if (!this.carousel) return
      if (this.$refs.carousel == undefined) return
      if (this.$refs.carousel.scrollLeft == undefined) return
      if (!this.$refs.carousel.offsetWidth) return
      const maxReduction = 10
      this.carouselScrollMax = this.$refs.carousel.scrollWidth - this.$refs.carousel.offsetWidth - maxReduction
      this.carouselScrollProgress = (this.$refs.carousel.scrollLeft / this.carouselScrollMax) * 100
      this.carouselEdgeLeft = this.$refs.carousel.scrollLeft + this.spacingHorizontal
      this.carouselEdgeRight = this.$refs.carousel.scrollLeft + this.$refs.carousel.offsetWidth - this.spacingHorizontal
      this.checkCarouselVisibleItems()
    },
    checkCarouselVisibleItems () {
      /**
       * Check which items should be visible
       */
      if (!this.carousel) return
      if (!this.items || !this.items.length) return
      let visibleItems = []
      this.items.forEach((item, index) => {
        const element = item.$el || item.elm
        if (!element) return
        let isItemVisible = false

        // Check if center of the element is inside the carousel edges
        const itemCenter = element.offsetLeft + (element.offsetWidth * 0.5)
        if (this.carouselEdgeLeft <= itemCenter && itemCenter <= this.carouselEdgeRight) {
          isItemVisible = true
        }

        // Element has no width at the moment; use its previous state
        if (element.offsetWidth == 0) {
          isItemVisible = this.carouselItemsVisible.includes(index)
        }

        // All items should be visible on small viewports
        if (!this.$viewport[interactionBreakpoint + 'AndUp']){
          isItemVisible = true
        }

        // Add item to array of visible items
        if (isItemVisible) {
          visibleItems.push(index)
        }
        this.setCarouselItemVisibility(item, isItemVisible)
      })
      this.carouselItemsVisible = visibleItems
    },
    async setCarouselItemVisibility (item, isItemVisible) {
      /**
       * Hide or show an item in the carousel
       */
      if (!item) return
      if (!this.carousel) return
      if (isItemVisible) {
        if (item.$options && item.$options.name === 'EoCard') {
          item.isVisible = true
        } else {
          const element = item.$el || item.elm
          if (!element || !element.style) return
          element.style.removeProperty('opacity')
          element.style.removeProperty('pointer-events')
          element.style.removeProperty('transition')
        }
      } else {
        if (item.$options && item.$options.name === 'EoCard') {
          item.isVisible = false
        } else {
          const element = item.$el || item.elm
          if (!element || !element.style) return
          element.style.opacity = '0'
          element.style.pointerEvents = 'none'
          element.style.transition = 'opacity 0.2s cubic-bezier(0.25, 0.8, 0.25, 1)'
        }
      }
    },
    checkCarouselLoadMore: throttle(function () {
      /** 
       * Check if we have to emit a load more event
       */
      if (!this.carousel) return
      if (this.$refs.carousel == undefined) return
      if (this.$refs.carousel.scrollLeft <= this.carouselPreviousLoadMore) return
      if (this.$refs.carousel.scrollLeft + this.$refs.carousel.offsetWidth <= Math.max(0, this.carouselScrollMax - this.$refs.carousel.offsetWidth)) return
      /**
       * Emitted when the user reaches last page before the end of the list
       * @event loadMore
       */
      this.$emit('load-more', true)
      this.carouselPreviousLoadMore = this.$refs.carousel.scrollLeft
    }, 1000),
    onCarouselResize: throttle(function () {
      /** 
       * Handle resize event
       */
      this.checkCarouselVisibleItems()
    }, 50),
    onCarouselScroll: throttle(function () {
      /** 
       * Handle scroll event
       */
      this.trackCarouselState()
      this.checkCarouselLoadMore()
    }, 50),
    scrollCarousel (direction) {
      /** 
       * Scroll the carousel forward or backward (-1 or 1)
       */
      if (this.carouselItemsVisible.length <= 0) return
      const amountItemsToScroll = this.carouselItemsVisible.length
      // Multiple items visible
      if (direction > 0) {
        // Go forward
        this.scrollCarouselToIndex(this.carouselItemsVisible[0] + amountItemsToScroll, this.carouselScrollMax)
      } else {
        // Go backward
        this.scrollCarouselToIndex(this.carouselItemsVisible[0] - amountItemsToScroll, 0)
      }
    },
    scrollCarouselToIndex (index, fallbackPosition = null) {
      /** 
       * Scroll carousel to specific element in list or a fallback scroll position
       */
      if (index === undefined) return
      const itemToScrollTo = this.items[index]
      const elementToScrollTo = itemToScrollTo ? (itemToScrollTo.$el || itemToScrollTo.elm) : null
      let positionToScrollTo = elementToScrollTo ? elementToScrollTo.offsetLeft - this.spacingHorizontal : fallbackPosition
      if (positionToScrollTo === undefined || positionToScrollTo === null) return
      this.scrollCarouselToOffset(positionToScrollTo)
    },
    scrollIndexIntoView (index) {
      /** 
       * Make sure the element is visible inside the carousel
       */
      if (index === undefined) return
      const itemToScrollTo = this.items[index]
      const elementToScrollTo = itemToScrollTo ? (itemToScrollTo.$el || itemToScrollTo.elm) : undefined
      if (!elementToScrollTo) return
      elementToScrollTo.scrollIntoView()
    },
    scrollCarouselToOffset (positionToScrollTo) {
      /** 
       * Scroll carousel to specific offset position
       */
      if (positionToScrollTo === undefined || positionToScrollTo == null) return
      if (positionToScrollTo > this.carouselScrollMax) {
        positionToScrollTo = this.carouselScrollMax
      }
      this.$refs.carousel.scrollTo(positionToScrollTo, 0)
    },
  },
}
